import * as Menu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, LogOut, MonitorSmartphone, ShieldCheck, UserRound } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Avatar } from "@/components/atoms/Avatar";
import { useAuth } from "@/features/auth/AuthProvider";
import { ROUTES } from "@/app/router/routes";
import { ConfirmDialog } from "./ConfirmDialog";

const itemClass =
  "flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-muted";

export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, signOut } = useAuth();
  const [confirm, setConfirm] = useState<null | "this" | "all">(null);
  const [busy, setBusy] = useState(false);
  if (!me) return null;

  const runSignOut = async () => {
    setBusy(true);
    try {
      await signOut({ everywhere: confirm === "all" });
      toast.success(confirm === "all" ? t("auth.signedOutEverywhere") : t("auth.signedOut"));
      navigate(ROUTES.LOGIN, { replace: true });
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  return (
    <>
      <Menu.Root>
        <Menu.Trigger
          className="flex items-center gap-2.5 rounded-xl p-1 pr-2 text-left hover:bg-muted"
          aria-label={t("userMenu.open")}
        >
          <Avatar name={me.user.fullName} src={me.user.avatarUrl} size="sm" />
          <span className="hidden min-w-0 sm:block">
            <span className="block max-w-40 truncate text-sm font-semibold">{me.user.fullName}</span>
            <span className="block max-w-40 truncate text-xs text-muted-foreground">{me.user.email ?? me.user.mobile}</span>
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content align="end" sideOffset={8} className="z-50 w-60 animate-fade-in-up rounded-xl border bg-card p-1.5 shadow-lifted">
            <Menu.Item className={itemClass} onSelect={() => navigate(ROUTES.ACCOUNT_PROFILE)}>
              <UserRound className="h-4 w-4" aria-hidden /> {t("nav.profile")}
            </Menu.Item>
            <Menu.Item className={itemClass} onSelect={() => navigate(ROUTES.ACCOUNT_SECURITY)}>
              <ShieldCheck className="h-4 w-4" aria-hidden /> {t("nav.security")}
            </Menu.Item>
            <Menu.Separator className="my-1 h-px bg-border" />
            <Menu.Item className={itemClass} onSelect={() => setConfirm("this")}>
              <LogOut className="h-4 w-4" aria-hidden /> {t("userMenu.signOut")}
            </Menu.Item>
            <Menu.Item className={`${itemClass} text-destructive`} onSelect={() => setConfirm("all")}>
              <MonitorSmartphone className="h-4 w-4" aria-hidden /> {t("userMenu.signOutAll")}
            </Menu.Item>
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>

      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
        tone={confirm === "all" ? "danger" : "default"}
        title={confirm === "all" ? t("userMenu.confirmAllTitle") : t("userMenu.confirmTitle")}
        description={confirm === "all" ? t("userMenu.confirmAllBody") : t("userMenu.confirmBody")}
        confirmLabel={confirm === "all" ? t("userMenu.signOutAll") : t("userMenu.signOut")}
        busyLabel={t("loader.signingOut")}
        busy={busy}
        onConfirm={runSignOut}
      />
    </>
  );
}
